// NodeForms.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Services
import { DEFAULT_FORM_KEY } from 'services/forms/FormModel';

// Hooks
import useNodeForms from 'node/hooks/useNodeForms';

// CSS
import './NodeForms.css';

// Components
import Tabs from 'components/tabs/Tabs';
import FormRenderer from 'form/FormRenderer';
import NodeFormModal from 'node/forms/NodeFormModal';
import NodeFormsList from 'node/forms/NodeFormsList';
import NodeDetails from 'node/details/NodeDetails';

function normalizeString(value) {
  return String(value || '').trim();
}

/**
 * NodeForms
 *
 * Renders the node-native Forms surface.
 * Compact (Peek): form list → modal. Expanded (Node): tabs + inline form.
 */
const NodeForms = React.memo(({
  node,
  onCoverSaved,
  onLocationSaved,
  onSubtitleSaved,
  onTagsSaved,
  onNodeChange,
  addon = 'expanded',
}) => {
  // ----- CONTEXT ----- //

  const baseKey = normalizeString(node?.baseKey);
  const typeKey = normalizeString(node?.type);
  const isCompact = addon === 'compact';

  // ----- HOOKS ----- //

  const { forms, defaultForm, isReady } = useNodeForms({
    typeKey,
    baseKey,
  });

  // ----- STATE ----- //

  const [selectedFormKey, setSelectedFormKey] = useState('');
  const [modalFormKey, setModalFormKey] = useState('');

  // ----- MEMOS ----- //

  const visibleForms = useMemo(() => {
    if (forms.length) return forms;

    return [{
      description: 'Default metadata form for this record.',
      isDefault: true,
      key: DEFAULT_FORM_KEY,
      title: 'Details',
    }];
  }, [forms]);

  // Peek About already covers the default form — only list custom forms there.
  const listForms = useMemo(() => {
    if (!isCompact) return visibleForms;
    return visibleForms.filter((form) => !(
      Boolean(form?.isDefault) || normalizeString(form?.key) === DEFAULT_FORM_KEY
    ));
  }, [isCompact, visibleForms]);

  const formTabs = useMemo(() => (
    visibleForms.map((form) => ({
      iconKey: form?.isDefault ? 'dynamic_form' : 'edit_note',
      key: form.key,
      label: form?.title || 'Untitled Form',
      onClick: () => setSelectedFormKey(form.key),
    }))
  ), [visibleForms]);

  const selectedForm = useMemo(() => {
    const cleanedSelectedFormKey = normalizeString(selectedFormKey);
    if (!cleanedSelectedFormKey) {
      return visibleForms[0] || null;
    }

    return visibleForms.find((form) => form.key === cleanedSelectedFormKey) || visibleForms[0] || null;
  }, [selectedFormKey, visibleForms]);

  const modalForm = useMemo(() => {
    const cleanedModalFormKey = normalizeString(modalFormKey);
    if (!cleanedModalFormKey) return null;
    return listForms.find((form) => form.key === cleanedModalFormKey) || null;
  }, [listForms, modalFormKey]);

  // ----- EFFECTS ----- //

  useEffect(() => {
    if (isCompact) {
      if (!listForms.length) {
        setModalFormKey('');
        return;
      }

      if (modalFormKey && !listForms.some((form) => form.key === modalFormKey)) {
        setModalFormKey('');
      }
      return;
    }

    if (!visibleForms.length) {
      setSelectedFormKey('');
      return;
    }

    const hasCurrentSelection = visibleForms.some((form) => form.key === selectedFormKey);
    if (hasCurrentSelection) return;

    const preferredKey = defaultForm?.key || visibleForms[0]?.key || '';
    setSelectedFormKey(preferredKey);
  }, [defaultForm?.key, isCompact, listForms, modalFormKey, selectedFormKey, visibleForms]);

  // ----- HANDLERS ----- //

  const handleSelectForm = useCallback((formKey) => {
    const cleanedFormKey = normalizeString(formKey);
    if (cleanedFormKey) {
      setSelectedFormKey(cleanedFormKey);
    }
  }, []);

  const handleOpenFormModal = useCallback((formKey) => {
    const cleanedFormKey = normalizeString(formKey);
    if (!cleanedFormKey) return;
    setModalFormKey(cleanedFormKey);
  }, []);

  const handleCloseFormModal = useCallback(() => {
    setModalFormKey('');
  }, []);

  const hasPersistedLayout = Boolean(
    selectedForm?.layout
    && selectedForm?.blocksByKey
    && Object.keys(selectedForm.blocksByKey).length,
  );

  // ----- RENDER (Peek compact: list → modal) ----- //

  if (isCompact) {
    return (
      <div className="node-forms-container node-forms-container-compact">
        {!isReady ? null : (
          <NodeFormsList
            forms={listForms}
            onSelectForm={handleOpenFormModal}
          />
        )}

        <NodeFormModal
          isOpen={Boolean(modalForm)}
          form={modalForm}
          node={node}
          onClose={handleCloseFormModal}
          onChange={onNodeChange}
          onCoverSaved={onCoverSaved}
          onLocationSaved={onLocationSaved}
          onSubtitleSaved={onSubtitleSaved}
          onTagsSaved={onTagsSaved}
        />
      </div>
    );
  }

  // ----- RENDER (Node expanded: tabs + inline form) ----- //

  return (
    <div className="node-forms-container">

      <div className="node-forms-tab-bar">
        <div className="node-forms-tabs-wrapper">
          <div className="node-forms-tabs-branch">
            └─
          </div>

          <Tabs
            tabs={formTabs.map((tab) => ({
              ...tab,
              onClick: () => handleSelectForm(tab.key),
            }))}
            selectedKey={selectedForm?.key || ''}
            showLabels={true}
          />
        </div>
      </div>

      <div className="node-forms-content">
        {!isReady ? null : hasPersistedLayout ? (
          <FormRenderer
            formDoc={selectedForm}
            node={node}
            onChange={onNodeChange}
            onCoverSaved={onCoverSaved}
            onLocationSaved={onLocationSaved}
            onSubtitleSaved={onSubtitleSaved}
            onTagsSaved={onTagsSaved}
          />
        ) : (
          <NodeDetails
            node={node}
            onCoverSaved={onCoverSaved}
            onLocationSaved={onLocationSaved}
            onNodeChange={onNodeChange}
            onSubtitleSaved={onSubtitleSaved}
            onTagsSaved={onTagsSaved}
          />
        )}
      </div>
    </div>
  );
});

NodeForms.displayName = 'NodeForms';

export default NodeForms;
